'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';

import ChooseFoodLabelModal from '@/components/ChooseFoodLabelModal';
import ProductQuantity from '@/components/EzShip/ProductQuantity';
import { useShoppingCartContext } from '@/components/Providers/ShoppingCartProvider';
import { HeadingFive, HeadingFour } from '@/components/Typography/Heading';
// import { NEXT_MEDIA_PATH } from '@/config/env';
import { PaymentType } from '@/helpers/constants/shopping';
import { useDetectClickOutside } from '@/helpers/hooks';
import useEzShip from '@/helpers/hooks/useEzShip';
import useWebAlias from '@/helpers/hooks/useWebalias';
import cn from '@/helpers/utils/classNames';
import { roundToTwoDecimalPlaces } from '@/helpers/utils/numberHelpers';
import { useAppContext } from '@/layouts/AppProvider';
import { useAppSelector } from '@/store';
import { useRemoveLabelFromEzShipItemMutation } from '@/store/api/ezShip';
import { CartItem } from '@/types/Cart';
import { EzShipItem } from '@/types/EzShip';

import Quantity from '../../Quantity';
import Paragraph from '../../Typography/Paragraph';
import { useStepper } from '../CartFreeShippingContainer/StepperContainer';
import { useCartListContext } from '../CartList';

import '@/styles/globals.v2.css';

import { NEXT_PUBLIC_PRODUCT_IMAGE_BASE_URL } from '@/config/env';
import { nextImageWrapper } from '@/helpers/utils/cloudflare';
import { BASIC_START_KIT_ITEM_CODE, BUILD_YOUR_PACK_ITEM_CODE } from '@/helpers/utils/enrollmentFlowTracker';

import ConditionerLinkComp from '../../ConditionerLinkComp';
import TrcukOneIcon from '../CartFreeShippingContainer/icons/TrcukOneIcon';
import TrcukThreeIcon from '../CartFreeShippingContainer/icons/TrcukThreeIcon';
import TrcukTwoIcon from '../CartFreeShippingContainer/icons/TrcukTwoIcon';
import Swtich from './Swtich';

interface CartListItemProps {
  cartItem: Partial<CartItem>;
}

const EZSHIP_DISCOUNT_ITEM = 'PcShipDisc';

const CartListItem = ({ cartItem }: CartListItemProps) => {
  const {
    name,
    quantity,
    price,
    totalPrice,
    image,
    weight,
    isEZShip,
    id,
    itemId,
    label,
    isFoodItem,
    paymentType,
    pawPoint,
    sampleCredits,
    itemCode,
    truckIconFreeShippingQualified,
  } = cartItem;
  const { variant } = useCartListContext();
  const { isBackOffice } = useAppContext();
  const { ezShipId, generateURL: generateEzShipURL } = useEzShip();
  const { webalias, generateURL: generateWebaliasURL } = useWebAlias();

  const { activeStep, setCurrentActiveStep, totalSteps } = useStepper(0, 3);
  const {
    onUpdateEZShip,
    onUpdateQuantity,
    onRemoveFromCart,
    onRemoveFromEzShip,
    onAddToAutoOrder,
    onUpdateEzShipCartQuantity,
    qualifiedItemsCount,
  } = useShoppingCartContext();

  const isCompact = variant === 'compact';
  const isEzShipCompact = variant === 'ezship-compact';
  const [isOpen, setIsOpen] = useState(false);
  const popperRef = useRef<HTMLDivElement | null>(null);

  const [isOpenFoodLabelModal, setIsOpenFoodLabelModal] = useState(false);
  const [isEzshipToggleLoading, setIsEzshipToggleLoading] = useState(false);

  useDetectClickOutside(() => setIsOpen(false), popperRef);

  const handleToggleTooltip = () => setIsOpen(prev => !prev);

  const imageHeight = isCompact ? 114 : isEzShipCompact ? 90 : 160;
  const imageWidth = isCompact ? 114 : isEzShipCompact ? 90 : 160;

  const showFoodLabel = useMemo(() => !isCompact && isFoodItem, [isFoodItem, isCompact]);

  const existingLabels = useAppSelector(state => state.slices.ezShipSlice.existingLables);

  const labelForCurrentItem = existingLabels?.appliedLables.find(label => label.itemCode === (itemCode || itemId));

  const detailedLabelForCurrentItem = existingLabels?.allLabels.find(
    label => label.petFoodLabelGroupID === labelForCurrentItem?.labelID
  );

  const [removeEzShipFoodLabel, { isLoading: isRemovingEzShipFoodLabel }] = useRemoveLabelFromEzShipItemMutation();

  const autoOrderID = +ezShipId;

  const productPrice = useMemo(() => {
    switch (paymentType) {
      case PaymentType.CREDIT_CARD:
        return `$${roundToTwoDecimalPlaces(price ?? 0)}`;
      case PaymentType.PAW_POINT:
        return `${pawPoint} PP`;
      case PaymentType.SAMPLE_CREDIT:
        return `${sampleCredits} SC`;
      default:
        return `$${roundToTwoDecimalPlaces(price ?? 0)}`;
    }
  }, [price, pawPoint, paymentType, sampleCredits]);

  const isEzShipDiscountItem = cartItem.itemCode == EZSHIP_DISCOUNT_ITEM;

  const isEnrollmentPackItem = useMemo(() => {
    const code = String(cartItem.itemCode || cartItem.id || '');
    return code === BUILD_YOUR_PACK_ITEM_CODE || code === BASIC_START_KIT_ITEM_CODE;
  }, [cartItem.itemCode, cartItem.id]);

  const itemHref = isEnrollmentPackItem
    ? ''
    : generateWebaliasURL(
        generateEzShipURL(`/product/${encodeURIComponent(cartItem.itemCode || cartItem.id || '')}`, ezShipId),
        isBackOffice ? 'shopping' : webalias
      );

  const logRemoveFromCartGTM = (itemId: any) => {
    try {
      if (itemId && itemId != '' && cartItem) {
        const itemToBeRemoved = cartItem.itemCode == itemId || cartItem.id == itemId || cartItem.itemId == itemId;
        if (itemToBeRemoved && globalThis?.window?.dataLayer) {
          globalThis?.window?.dataLayer.push({ ecommerce: null });
          globalThis?.window?.dataLayer.push({
            event: 'remove_from_cart',
            ecommerce: {
              currency: 'USD',
              value: cartItem.totalPrice,
              items: [
                {
                  item_id: cartItem.id,
                  item_name: cartItem.name,
                  index: 0,
                  item_category: cartItem.category,
                  price: cartItem.price,
                  quantity: cartItem.quantity,
                },
              ],
            },
          });
        }
      }
    } catch {
      console.log('Failed to log remove_from_cart to gtm');
    }
  };

  const productImage = nextImageWrapper(`${NEXT_PUBLIC_PRODUCT_IMAGE_BASE_URL}/${isEzShipCompact ? itemCode : id}`);

  const totalActiveSteps = useMemo(() => {
    const ezShipItems = +(qualifiedItemsCount ?? 0);

    if (ezShipItems >= totalSteps) return totalSteps;

    return ezShipItems;
  }, [qualifiedItemsCount, totalSteps]);

  useEffect(() => {
    setCurrentActiveStep(totalActiveSteps);
  }, [totalActiveSteps, setCurrentActiveStep]);

  const handleLoadImage = () => {
    return productImage;
  };

  return (
    <>
      {isOpenFoodLabelModal && (
        <ChooseFoodLabelModal
          isOpen={isOpenFoodLabelModal}
          cartItem={cartItem as CartItem}
          onClose={() => setIsOpenFoodLabelModal(false)}
        />
      )}
      <li
        className={cn('v2-flex v2-gap-5 v2-py-5 sm:v2-pb-[38px] sm:v2-pt-[34px]', {
          'v2-h-full v2-gap-0': isEzShipCompact,
          'v2-items-stretch': !isEzShipCompact,
          'v2-border-b-2 v2-border-b-[#f7f6f2] sm:v2-last:border-b-0 !v2-gap-3': !isCompact,
        })}
      >
        <ConditionerLinkComp
          href={itemHref}
          className={cn(
            'v2-rounded v2-bg-[#f3f6f5] v2-p-1 v2-h-[74px] sm:v2-h-[74px] sm:v2-w-[74px] md:v2-h-[94px] md:v2-w-[94px] lg:v2-h-[160px] lg:v2-w-[160px]',
            {
              'v2-flex v2-h-[110px] v2-w-[110px] v2-items-center v2-justify-center md:v2-h-[80px] md:v2-w-[80px] lg:v2-h-[124px] lg:v2-w-[124px]':
                isCompact,
              'v2-bg-transparent': isEzShipCompact,
              '!v2-flex !v2-justify-center !v2-items-center lg:v2-h-[154px] lg:v2-w-[154px] !v2-cursor-default':
                isEnrollmentPackItem,
            }
          )}
          isLink={isEnrollmentPackItem}
        >
          <Image
            src={productImage}
            loader={handleLoadImage}
            alt={name || ''}
            width={imageWidth}
            height={imageHeight}
            className={cn('v2-w-[65px] sm:v2-w-[74px] md:v2-w-[160px]', {
              'v2-h-[100px] v2-w-[100px] md:v2-h-[70px] md:v2-w-[70px] lg:v2-h-[114px] lg:v2-w-[114px]': isCompact,
              'sm:v2-h-[50px] sm:v2-w-[50px] md:v2-h-[90px] md:v2-w-[90px]': isEzShipCompact,
              '!v2-w-auto': isEnrollmentPackItem,
            })}
          />
        </ConditionerLinkComp>
        <div
          className={cn('v2-flex v2-flex-grow v2-items-start v2-justify-between v2-gap-2', {
            'v2-gap-0': isEzShipCompact,
          })}
        >
          <div
            className={cn(
              'v2-flex v2-flex-grow v2-flex-row xs:v2-flex-grow-0 xs:v2-flex-col sm:v2-flex-row v2-justify-between v2-gap-2 lg:v2-flex-row lg:v2-gap-0  xs:v2-w-full',
              {
                'v2-w-full': isEzShipCompact,
              }
            )}
          >
            <div
              className={cn('v2-flex v2-flex-col v2-gap-2', {
                'v2-gap-1': isCompact,
                'sm:v2-w-[310px] md:v2-w-[310px] lg:v2-w-[310px] xs:v2-w-full v2-w-[200px]': !isCompact,
                '!v2-w-full v2-gap-0': isEzShipCompact,
              })}
            >
              <h3
                className={cn('v2-font-sweet-sans-pro v2-text-[#101828] v2-text-[20px] leading-[30px]', {
                  '!v2-font-sweet-sans-pro !v2-font-light sm:v2-mr-[4px] sm:!v2-text-[14px] md:v2-mr-[8px] md:!v2-text-[16px]':
                    isEzShipCompact,
                  'md:v2-text-base': isCompact,
                  'v2-mt-6': isEzShipDiscountItem,
                })}
              >
                {isEnrollmentPackItem ? name : <Link href={itemHref}>{name}</Link>}
              </h3>
              {!isCompact && !isEzShipCompact && (
                <HeadingFour className="v2-font-sweet-sans-pro v2-block xs:v2-block sm:v2-hidden">
                  {productPrice}
                </HeadingFour>
              )}
              <div>
                {isCompact && (
                  <Paragraph className="v2-text-[#333] v2-leading-[30px]">
                    <span className="v2-font-bold">Price: </span> <span>${totalPrice}</span>
                  </Paragraph>
                )}{' '}
                {!isEzShipCompact && (
                  <Paragraph
                    className={cn(
                      'v2-font-sweet-sans-pro v2-mt-[2px] v2-leading-6 v2-font-normal v2-mb-0 v2-text-gray-900 v2-text-base'
                    )}
                  >
                    <span>Item: </span> <span>{id}</span>
                  </Paragraph>
                )}
                {!isEzShipCompact && !isEnrollmentPackItem && (
                  <Paragraph
                    className={cn(
                      'v2-font-sweet-sans-pro v2-leading-6 v2-font-normal v2-mb-0 v2-text-[#101828] v2-text-base'
                    )}
                  >
                    <span>Size: </span> <span>{weight}</span>
                  </Paragraph>
                )}
              </div>

              {/* {!isCompact && !isEzShipCompact && (
                <div className="v2-flex-grow-0 v2-items-center v2-gap-3 sm:v2-flex lg:v2-hidden lg:v2-flex-grow lg:v2-items-start lg:v2-justify-evenly lg:v2-gap-0">
                  <div className="">
                    <Quantity
                      value={quantity}
                      onChange={value => onUpdateQuantity(itemCode || itemId || '', value)}
                      className="!v2-min-w-[110px] !v2-px-3"
                    />
                  </div>

                  <HeadingFour className="v2-font-sweet-sans-pro">{productPrice}</HeadingFour>
                </div>
              )} */}

              {!isCompact && !isEzShipCompact && !isEnrollmentPackItem && truckIconFreeShippingQualified !== 0 && (
                <div className="v2-flex v2-gap-2 v2-items-center">
                  {truckIconFreeShippingQualified === 1 && <TrcukOneIcon height="26" width="26" />}
                  {truckIconFreeShippingQualified === 2 && <TrcukTwoIcon height="26" width="26" />}
                  {truckIconFreeShippingQualified === 3 && <TrcukThreeIcon height="26" width="26" />}
                  <span className="v2-font-sweet-sans-pro v2-text-sm v2-text-[#154734]">Qualifying Item</span>
                </div>
              )}
              {!isCompact && !isEzShipCompact && cartItem.isEZShipEligible && (
                <div className="v2-flex v2-items-center v2-gap-2 v2-mt-[22px] sm:v2-mt-[22px] md:v2-mt-[41px]">
                  {/* <Checkbox
                    value={isEZShip || false}
                    containerClassName="v2-items-center"
                    childrenClassName="v2-flex v2-items-center v2-gap-2"
                    id={id || ''}
                    name={`ezship ${id}`}
                    onChange={() => onUpdateEZShip(itemCode || itemId || '', !isEZShip)}
                  >
                    <Paragraph className="v2-font-sweet-sans-pro-light sm:v2-text-base md:v2-text-lg">EZ-Ship</Paragraph>
                  </Checkbox> */}
                  <Swtich
                    value={isEZShip || false}
                    loading={isEzshipToggleLoading}
                    onChange={() => {
                      setIsEzshipToggleLoading(true);
                      onUpdateEZShip(itemCode || itemId || '', !isEZShip).finally(() => {
                        setTimeout(() => {
                          setIsEzshipToggleLoading(false);
                        }, 2000);
                      });
                    }}
                  />

                  {/* <div ref={popperRef}>
                    <Tooltip
                      minWidth="25rem"
                      open={isOpen}
                      variant="light"
                      PopperProps={{
                        disablePortal: true,
                      }}
                      disableHoverListener
                      disableFocusListener
                      disableTouchListener
                      title={
                        <div className="v2-relative">
                          <span
                            className="v2-absolute v2-right-2 v2-top-2 v2-cursor-pointer"
                            onClick={handleToggleTooltip}
                          >
                            <CloseOutlined className="v2-p-0 v2-text-primary" fontSize="small" />
                          </span>

                          <ul className="v2-p-7">
                            <li className="v2-mb-5 v2-flex v2-items-start v2-justify-center v2-gap-5">
                              <Image src={EZShipBusImage} alt="Bus Image" />
                              <Paragraph>
                                Unlock special discounts and receive FREE SHIPPING with 3 qualifying items.
                              </Paragraph>
                            </li>
                            <li className="v2-mb-5 v2-flex v2-items-start v2-justify-center v2-gap-5">
                              <Image src={EZShipCalendarImage} alt="Calendar Image" />
                              <Paragraph>
                                Choose how often you&apos;d like product delivered to your doorstep.
                              </Paragraph>
                            </li>
                            <li className="v2-flex v2-items-start v2-justify-center v2-gap-5">
                              <Image src={EZShipPawImage} alt="Paw Image" />
                              <Paragraph>Earn Paw Club points to purchase toys.</Paragraph>
                            </li>
                          </ul>
                        </div>
                      }
                    >
                      <Image
                        src={ImageInfo}
                        alt="Info Image"
                        onClick={handleToggleTooltip}
                        className="v2-cursor-point v2-h-[18px] v2-w-[18px]"
                      />
                    </Tooltip>
                  </div> */}
                </div>
              )}

              {/* {showFoodLabel && !isEzShipCompact && (
                <>
                  {!label?.labelId ? (
                    <Paragraph
                      variant="orange"
                      className="v2-ml-6 v2-cursor-pointer sm:v2-text-base sm:v2-leading-normal"
                      onClick={() => setIsOpenFoodLabelModal(true)}
                    >
                      Add custom food label
                    </Paragraph>
                  ) : (
                    <Link
                      href="#"
                      className="v2-font-sweet-sans-pro v2-ml-6 v2-text-base v2-text-[#0984ff]"
                      onClick={e => {
                        e.preventDefault();
                        setIsOpenFoodLabelModal(true);
                      }}
                    >
                      Edit Label
                    </Link>
                  )}
                </>
              )} */}

              {isEzShipCompact && !isEzShipDiscountItem && (
                <>
                  {!isEnrollmentPackItem && (
                    <div className="v2-my-[4px]">
                      <ProductQuantity
                        className="v2-rounded-md"
                        quantity={(cartItem as EzShipItem).quantity}
                        minValue={1}
                        limit={Infinity}
                        onChange={qtty => {
                          // const quantity = qtty - (cartItem as EzShipItem).quantity;
                          onUpdateEzShipCartQuantity({
                            autoOrderID: +ezShipId,
                            itemCode: itemCode || itemId || '',
                            quantity: qtty,
                          });
                          // onAddToAutoOrder({
                          //   autoOrderID: +ezShipId,
                          //   items: [
                          //     {
                          //       isEzShip: true,
                          //       numberOfEZShipDays: 0,
                          //       itemCode: itemCode || itemId || '',
                          //       quantity,
                          //     },
                          //   ],
                          // });
                        }}
                      />
                    </div>
                  )}
                  {/* {showFoodLabel && (
                    <>
                      {!labelForCurrentItem ? (
                        <p
                          className="v2-cursor-pointer v2-text-primary v2-underline sm:v2-text-base sm:v2-leading-normal"
                          onClick={() => setIsOpenFoodLabelModal(true)}
                        >
                          Add custom food label
                        </p>
                      ) : (
                        <div className="v2-flex v2-flex-row v2-gap-5">
                          <p
                            title={'Remove ' + detailedLabelForCurrentItem?.name}
                            className="v2-cursor-pointer v2-text-primary v2-underline sm:v2-text-base sm:v2-leading-normal"
                            onClick={async () => {
                              if (isRemovingEzShipFoodLabel) return;
                              const resp = await removeEzShipFoodLabel({
                                autoOrderID,
                                itemCode: +(itemCode || itemId || 0),
                                recordnumber: labelForCurrentItem.recordNumber,
                              }).unwrap();
                            }}
                          >
                            {
                              <span className="v2-flex v2-flex-row v2-gap-2">
                                {detailedLabelForCurrentItem?.petFoodLabelGroupDetails
                                  .filter(d => d.imageVisible)
                                  .map(img => (
                                    <Image
                                      key={JSON.stringify(img)}
                                      src={`${NEXT_MEDIA_PATH}${img.petImage}`}
                                      alt={detailedLabelForCurrentItem?.name || ''}
                                      width={22}
                                      height={22}
                                      className="v2-rounded-full"
                                    />
                                  ))}

                                <span>{detailedLabelForCurrentItem?.name || ''}</span>
                                <Image
                                  src={`${NEXT_MEDIA_PATH}/Content/images/EZ-ship-detail-new/error-cross-icon.svg`}
                                  alt={'remove' + detailedLabelForCurrentItem?.name}
                                  width={15}
                                  height={15}
                                  className="v2-rounded-full sm:v2-mr-3 md:v2-mr-0"
                                />
                              </span>
                            }
                          </p>
                          <span className="v2-border-r"></span>
                          <p
                            className="v2-cursor-pointer v2-text-primary v2-underline sm:v2-text-base sm:v2-leading-normal"
                            onClick={() => setIsOpenFoodLabelModal(true)}
                          >
                            Edit Label
                          </p>
                        </div>
                      )}
                    </>
                  )} */}
                </>
              )}
            </div>
            {!isCompact && !isEzShipCompact && (
              <div className="v2-items-stretch sm:v2-items-start v2-justify-between v2-gap-3 xs:v2-block sm:v2-flex lg:v2-flex lg:v2-items-start v2-w-[232px] lg:gap-0">
                {!isEnrollmentPackItem ? (
                  <Quantity
                    value={quantity}
                    minValue={1}
                    limit={25}
                    withDebouncing
                    readonly={false}
                    onChange={value => onUpdateQuantity(itemCode || itemId || '', value)}
                    className="!v2-min-w-[110px] !v2-max-w-[160px] v2-pt-[8px] !v2-px-3 v2-my-5 xs:v2-my-5 sm:v2-my-0 md:v2-my-0"
                  />
                ) : (
                  <div className="!v2-min-w-[110px] !v2-max-w-[160px]" />
                )}

                <div className="v2-flex v2-h-full v2-flex-col v2-justify-between">
                  <p className="v2-font-sweet-sans-pro-medium v2-font-normal v2-text-base v2-text-[#154734] v2-hidden xs:v2-hidden sm:v2-block text-right">
                    {productPrice}
                  </p>{' '}
                  {!isEzShipCompact && !isEnrollmentPackItem && (
                    <Link
                      href="#"
                      className="v2-underline v2-font-sweet-sans-pro-semi-bold v2-text-[#154734] v2-text-base self-start"
                      onClick={event => {
                        event.preventDefault();
                        onRemoveFromCart(itemCode || itemId || '');
                        logRemoveFromCartGTM(itemCode || itemId || '');
                      }}
                    >
                      Remove Item
                    </Link>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {isEzShipCompact && !isEzShipDiscountItem && (
          <div className="v2-flex v2-flex-col v2-gap-6">
            <HeadingFive
              variant="dark"
              className="!v2-font-sweet-sans-pro !v2-font-medium sm:!v2-text-[14px] md:!v2-text-[16px]"
            >
              ${price}
            </HeadingFive>
            {!isEnrollmentPackItem && (
              <Link
                href={'#'}
                className="v2-font-sweet-sans-pro v2-font-light v2-leading-[30px] v2-text-[#d7575b] v2-underline v2-underline-offset-1 sm:!v2-text-[14px] md:!v2-text-[16px]"
                onClick={event => {
                  event.preventDefault();
                  onRemoveFromEzShip({ autoOrderId: ezShipId, itemCode: itemCode || itemId || '' });
                }}
              >
                Remove
              </Link>
            )}
          </div>
        )}
      </li>
    </>
  );
};

export default CartListItem;
